// Dubai guide: district cooling vs split AC.
const content = {
    slug: 'district-cooling-vs-split-ac',
    title: 'District Cooling vs Split AC in Dubai | Chiller Fees Explained',
    description: 'District cooling vs your own split AC in Dubai: how each works, how chiller fees are charged, and what you can and can\'t service yourself.',
    eyebrow: 'Dubai · AC guide',
    h1: 'District Cooling vs Split AC in Dubai',
    lead: 'Two apartments in the same area can be cooled in completely different ways - and that changes your bills, what can go wrong, and who fixes it. Here is how to tell which you have.',
    datePublished: '2026-10-03',
    image: 'blog/living/cool-air-from-aircon.jpg',
    imageAlt: 'Ceiling AC diffuser blowing cool air in a Dubai apartment',
    summary: {
        title: 'Quick way to tell',
        items: [
            'You get a separate cooling or chiller bill (from Empower, Emicool, Tabreed or the building): district cooling.',
            'There is an outdoor unit with a fan on your balcony, roof or a plant deck that belongs to your apartment: split system.',
            'Your DEWA bill jumps in summer and there is no separate cooling bill: almost certainly a split system.',
        ],
    },
    sections: [
        {
            title: 'How district cooling works',
            paragraphs: [
                'A central plant chills water and pumps it through insulated pipes to buildings across a district. In your apartment, a fan coil unit (FCU) above the false ceiling blows room air across a coil carrying that chilled water. A valve, controlled by your thermostat, opens and closes to adjust the cooling. There is no compressor and no refrigerant gas in your apartment.',
            ],
        },
        {
            title: 'How a split system works',
            paragraphs: [
                'Your apartment has its own refrigerant circuit: an indoor unit (wall-mounted or concealed ducted) and an outdoor condenser with a compressor. Electricity for the compressor goes on your DEWA bill, which is why summer bills rise so sharply.',
            ],
        },
        {
            title: 'Side by side',
            table: {
                columns: [
                    { key: 'topic', label: '' },
                    { key: 'dc', label: 'District cooling' },
                    { key: 'split', label: 'Split AC' },
                ],
                rows: [
                    { topic: 'Bills', dc: 'Separate cooling bill: capacity charge + consumption', split: 'Included in DEWA electricity' },
                    { topic: 'Gas refills', dc: 'Never - no refrigerant', split: 'Only if there is a leak' },
                    { topic: 'Noise', dc: 'Very quiet, no outdoor unit', split: 'Outdoor unit noise on balcony/roof' },
                    { topic: 'Common faults', dc: 'Dirty coil, stuck valve, thermostat', split: 'Dirty coil, low gas, capacitor, compressor' },
                    { topic: 'What you service', dc: 'Filter, coil, blower, drain of your FCU', split: 'Indoor unit plus outdoor unit' },
                ],
            },
        },
        {
            title: 'Understanding chiller fees',
            paragraphs: [
                'District cooling bills usually have two parts: a fixed capacity (or demand) charge based on your apartment\'s allocated cooling load, and a variable consumption charge for the cooling you actually use, measured in ton-hours by a meter. The capacity charge is payable whether or not you run the AC.',
                'In "chiller-free" buildings the landlord pays the cooling bill and the rent reflects it. If you are comparing apartments, ask which applies - it can make a large difference to your monthly costs.',
            ],
        },
        {
            title: 'What a dirty fan coil costs you',
            paragraphs: [
                'Even with district cooling, a dust-caked coil and filter mean the FCU has to run longer to cool the room, which raises the consumption part of your bill and leaves rooms warm. Cleaning the FCU - filter, coil, blower and drain - is your side of the system to look after.',
            ],
        },
    ],
    faqs: [
        {
            question: 'Can I get my district cooling AC serviced?',
            answer: 'Yes - the fan coil unit in your apartment needs the same cleaning as any indoor unit. The building\'s chilled-water supply and the plant are the provider\'s and building\'s responsibility.',
        },
        {
            question: 'Why is my district cooled apartment not cold?',
            answer: 'Check the filter and thermostat first. If the air is barely cool, the FCU\'s valve may be stuck or the building\'s supply may have an issue - report it to building management.',
        },
        {
            question: 'Is district cooling cheaper than split AC?',
            answer: 'It depends on the capacity charge and your usage. District cooling is usually more energy-efficient overall, but the fixed charge means low users don\'t always save.',
        },
    ],
    cta: {
        title: 'Fan coil or split, keep the coil clean',
        body: 'Coil cleaning restores airflow and cuts running time on both systems.',
        href: '/ae/ac-service-dubai/ac-coil-chemical-wash',
        label: 'See coil cleaning prices',
    },
}

export default content
